import { useState, useEffect, useCallback } from 'react';
import { knowledgeBaseShareService } from '../services/knowledgeBaseShareService';
import {
  KnowledgeBaseShare,
  KnowledgeBaseShareCreate,
  SharePermission,
} from '../schemas/knowledgeBase';

interface UseKnowledgeBaseSharesReturn {
  shares: KnowledgeBaseShare[];
  loading: boolean;
  error: string | null;
  addShare: (data: KnowledgeBaseShareCreate) => Promise<KnowledgeBaseShare>;
  updatePermission: (shareId: number, permission: SharePermission) => Promise<void>;
  removeShare: (shareId: number) => Promise<void>;
  refetch: () => Promise<void>;
}

/**
 * Custom hook for managing knowledge base shares with optimistic updates
 * @param knowledgeBaseId - The ID of the knowledge base
 */
export function useKnowledgeBaseShares(knowledgeBaseId: string): UseKnowledgeBaseSharesReturn {
  const [shares, setShares] = useState<KnowledgeBaseShare[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch shares on mount or when knowledgeBaseId changes
  useEffect(() => {
    const fetchShares = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await knowledgeBaseShareService.getShares(knowledgeBaseId);
        setShares(response.items);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch shares';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchShares();
  }, [knowledgeBaseId]);

  // Refetch shares
  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await knowledgeBaseShareService.getShares(knowledgeBaseId);
      setShares(response.items);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch shares';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [knowledgeBaseId]);

  // Optimistically add a share
  const addShare = useCallback(
    async (data: KnowledgeBaseShareCreate): Promise<KnowledgeBaseShare> => {
      // Create optimistic share with temporary data
      const optimisticId = Date.now();
      const optimisticShare: KnowledgeBaseShare = {
        id: optimisticId,
        knowledge_base_id: knowledgeBaseId,
        shared_with_user_id: data.shared_with_user_id,
        shared_with_user: {
          id: data.shared_with_user_id,
          email: '',
          full_name: '',
        },
        permission: data.permission,
        created_at: new Date().toISOString(),
      };

      // Optimistically add to state
      setShares(prev => [...prev, optimisticShare]);

      try {
        const result = await knowledgeBaseShareService.createShare(knowledgeBaseId, data);

        // Replace optimistic share with real data
        setShares(prev => prev.map(share => (share.id === optimisticId ? result : share)));

        return result;
      } catch (err) {
        // Revert on error
        setShares(prev => prev.filter(share => share.id !== optimisticId));
        throw err;
      }
    },
    [knowledgeBaseId]
  );

  // Optimistically update share permission
  const updatePermission = useCallback(
    async (shareId: number, permission: SharePermission) => {
      // Save previous state for potential rollback
      const previousShares = [...shares];

      // Optimistically update
      setShares(prev =>
        prev.map(share => (share.id === shareId ? { ...share, permission } : share))
      );

      try {
        const shareToUpdate = shares.find(s => s.id === shareId);
        if (!shareToUpdate) {
          throw new Error('Share not found');
        }

        await knowledgeBaseShareService.updateShare(knowledgeBaseId, shareId, {
          shared_with_user_id: shareToUpdate.shared_with_user_id,
          permission,
        });
      } catch (err) {
        // Revert on error
        setShares(previousShares);
        throw err;
      }
    },
    [knowledgeBaseId, shares]
  );

  // Optimistically remove a share
  const removeShare = useCallback(
    async (shareId: number) => {
      // Save previous state for potential rollback
      const previousShares = [...shares];

      // Optimistically remove
      setShares(prev => prev.filter(share => share.id !== shareId));

      try {
        await knowledgeBaseShareService.deleteShare(knowledgeBaseId, shareId);
      } catch (err) {
        // Revert on error
        setShares(previousShares);
        throw err;
      }
    },
    [knowledgeBaseId, shares]
  );

  return {
    shares,
    loading,
    error,
    addShare,
    updatePermission,
    removeShare,
    refetch,
  };
}
